/**
 * Raw WebGL program wrapper for the point-cloud passes (PotreeRenderer bypasses
 * three.js for octree nodes).
 *
 * Linking is asynchronous wherever the context exposes
 * KHR_parallel_shader_compile. The point-cloud shader is large (surface hash
 * lookups, clip volumes, classification, EDL), and on Windows Chrome hands it
 * to ANGLE's HLSL compiler: one new source variant costs ~3-5 s of GPU-process
 * CPU on an Intel iGPU. A synchronous `getProgramParameter(LINK_STATUS)` parks
 * the main thread for all of it — the multi-second freeze users saw when a
 * cloud's root node loads (its attribute defines change the source), when a
 * tool first picks (the pick pass is its own variant), or when switching to a
 * cloud Chrome's program cache has not seen. Chrome's cache is keyed by source
 * text, which is why re-opening a recent view never stalled.
 *
 * With the extension the compile still happens in the GPU process, but the
 * renderer only polls COMPLETION_STATUS_KHR (sub-millisecond) each frame and
 * keeps drawing with the previously linked program until the new one is
 * ready. Only `waitReady()` — used by the synchronous pick pass, which reads
 * pixels straight back — still blocks, and only for the compile's remaining
 * time. Contexts without the extension link synchronously as before.
 */

export const attributeLocations = {
  "position": { name: "position", location: 0 },
  "color": { name: "color", location: 1 },
  "rgba": { name: "color", location: 1 },
  "intensity": { name: "intensity", location: 2 },
  "classification": { name: "classification", location: 3 },
  "returnNumber": { name: "returnNumber", location: 4 },
  "return number": { name: "returnNumber", location: 4 },
  "returns": { name: "returnNumber", location: 4 },
  "numberOfReturns": { name: "numberOfReturns", location: 5 },
  "number of returns": { name: "numberOfReturns", location: 5 },
  "pointSourceID": { name: "pointSourceID", location: 6 },
  "source id": { name: "pointSourceID", location: 6 },
  "point source id": { name: "pointSourceID", location: 6 },
  "indices": { name: "indices", location: 7 },
  "normal": { name: "normal", location: 8 },
  "spacing": { name: "spacing", location: 9 },
  "gps-time": { name: "gpsTime", location: 10 },
  "aExtra": { name: "aExtra", location: 11 },
};

export class Shader {

  constructor(gl, name, vsSource, fsSource) {
    this.gl = gl;
    this.name = name;
    this.vsSource = vsSource;
    this.fsSource = fsSource;

    // source key → linked program with its location tables
    this.cache = new Map();
    // source key → program handed to the GPU process, not yet known to be linked
    this.inflight = new Map();
    // source key → error of a variant that failed to link, so it is not retried
    this.failed = new Map();
    this.parallel = typeof gl.getExtension === 'function'
      ? gl.getExtension('KHR_parallel_shader_compile')
      : null;

    this.vs = null;
    this.fs = null;
    this.program = null;
    // source key the adopted program was built from
    this.programKey = null;

    this.uniformLocations = {};
    this.attributeLocations = {};
    this.uniformBlockIndices = {};
    this.uniformBlocks = {};
    this.uniforms = {};

    this.update(vsSource, fsSource);
  }

  update(vsSource, fsSource) {
    this.vsSource = vsSource;
    this.fsSource = fsSource;
    // Built once per update: the sources are hundreds of KB and the key is
    // compared against `programKey` every frame, and again by `ready`.
    this.key = `${vsSource}, ${fsSource}`;

    this.linkProgram();
  }

  /** True when `program` was built from the current sources. */
  get ready() {
    return this.program !== null && this.programKey === this.key;
  }

  /**
   * Adopts the program for the current sources if it is linked, otherwise
   * starts (or keeps polling) its background link and leaves the previously
   * adopted program in place. Cheap enough to call every frame.
   */
  linkProgram() {
    this.resolve(false);
  }

  /**
   * Blocks until the program for the current sources is linked. Used by the
   * pick pass, whose caller reads the pixels back synchronously.
   */
  waitReady() {
    this.resolve(true);
  }

  resolve(block) {
    const key = this.key;
    if (this.programKey === key) {
      return;
    }

    const cached = this.cache.get(key);
    if (cached) {
      this.adopt(key, cached);
      return;
    }

    const error = this.failed.get(key);
    if (error) {
      throw error;
    }

    let job = this.inflight.get(key);
    if (!job) {
      job = this.startLink();
      this.inflight.set(key, job);
    }

    if (!block && this.parallel && !this.isComplete(job)) {
      // Still compiling in the GPU process; `program` stays the old variant
      // (or null before the first link, in which case the caller skips drawing).
      this.reapInflight(key);
      return;
    }

    this.adopt(key, this.finishLink(key, job));
  }

  isComplete(job) {
    return !!this.gl.getProgramParameter(job.program, this.parallel.COMPLETION_STATUS_KHR);
  }

  /**
   * Caches variants whose link finished while a different source was wanted
   * (e.g. a clip box added and removed again), so a switch back is instant.
   */
  reapInflight(currentKey) {
    for (const [key, job] of this.inflight) {
      if (key !== currentKey && this.isComplete(job)) {
        try {
          this.finishLink(key, job);
        } catch (error) {
          // Nothing draws with this variant right now; it throws when wanted.
          console.error(error);
        }
      }
    }
  }

  /** Hands the sources to the GPU process without waiting for the result. */
  startLink() {
    const gl = this.gl;

    const vs = gl.createShader(gl.VERTEX_SHADER);
    const fs = gl.createShader(gl.FRAGMENT_SHADER);
    const program = gl.createProgram();

    for (const name of Object.keys(attributeLocations)) {
      const location = attributeLocations[name].location;
      const glslName = attributeLocations[name].name;
      gl.bindAttribLocation(program, location, glslName);
    }

    gl.shaderSource(vs, this.vsSource);
    gl.compileShader(vs);
    gl.shaderSource(fs, this.fsSource);
    gl.compileShader(fs);

    gl.attachShader(program, vs);
    gl.attachShader(program, fs);
    gl.linkProgram(program);

    return { program, vs, fs, vsSource: this.vsSource, fsSource: this.fsSource };
  }

  /**
   * Waits for the link (LINK_STATUS blocks until the compile is done), reads
   * the location tables and caches the result under `key`.
   */
  finishLink(key, job) {
    const gl = this.gl;
    const { program, vs, fs } = job;

    this.inflight.delete(key);

    if (!gl.getProgramParameter(program, gl.LINK_STATUS)) {
      const error = this.compileError(vs, job.vsSource)
        ?? this.compileError(fs, job.fsSource)
        ?? `could not link program ${this.name}: ${gl.getProgramInfoLog(program)}`;

      gl.deleteProgram(program);
      gl.deleteShader(vs);
      gl.deleteShader(fs);
      // Remember the failure: relinking the same source every frame would leak
      // a program per attempt and keep the GPU process compiling.
      this.failed.set(key, error);

      throw error;
    }

    gl.detachShader(program, vs);
    gl.detachShader(program, fs);

    const linked = {
      program,
      vs,
      fs,
      attributeLocations: {},
      uniformLocations: {},
      uniforms: {},
      uniformBlockIndices: {},
      uniformBlocks: {},
    };

    { // attribute locations
      const numAttributes = gl.getProgramParameter(program, gl.ACTIVE_ATTRIBUTES);

      for (let i = 0; i < numAttributes; i++) {
        const attribute = gl.getActiveAttrib(program, i);
        const location = gl.getAttribLocation(program, attribute.name);

        linked.attributeLocations[attribute.name] = location;
      }
    }

    { // uniform locations
      const numUniforms = gl.getProgramParameter(program, gl.ACTIVE_UNIFORMS);

      for (let i = 0; i < numUniforms; i++) {
        const uniform = gl.getActiveUniform(program, i);
        const location = gl.getUniformLocation(program, uniform.name);

        linked.uniformLocations[uniform.name] = location;
        linked.uniforms[uniform.name] = {
          location: location,
          value: null,
        };
      }
    }

    // uniform blocks (WebGL 2 only)
    if (typeof gl.getActiveUniformBlockName === 'function') {
      const numBlocks = gl.getProgramParameter(program, gl.ACTIVE_UNIFORM_BLOCKS);

      for (let i = 0; i < numBlocks; i++) {
        const blockName = gl.getActiveUniformBlockName(program, i);
        const blockIndex = gl.getUniformBlockIndex(program, blockName);

        linked.uniformBlockIndices[blockName] = blockIndex;

        gl.uniformBlockBinding(program, blockIndex, blockIndex);
        const dataSize = gl.getActiveUniformBlockParameter(program, blockIndex, gl.UNIFORM_BLOCK_DATA_SIZE);

        const uBuffer = gl.createBuffer();
        gl.bindBuffer(gl.UNIFORM_BUFFER, uBuffer);
        gl.bufferData(gl.UNIFORM_BUFFER, dataSize, gl.DYNAMIC_READ);

        gl.bindBufferBase(gl.UNIFORM_BUFFER, blockIndex, uBuffer);

        gl.bindBuffer(gl.UNIFORM_BUFFER, null);

        linked.uniformBlocks[blockName] = {
          name: blockName,
          index: blockIndex,
          dataSize: dataSize,
          buffer: uBuffer
        };
      }
    }

    this.cache.set(key, linked);

    return linked;
  }

  /** The compile error message for `shader`, or null when it compiled. */
  compileError(shader, source) {
    const gl = this.gl;

    if (gl.getShaderParameter(shader, gl.COMPILE_STATUS)) {
      return null;
    }

    const info = gl.getShaderInfoLog(shader);
    const numberedSource = source.split("\n").map((a, i) => `${i + 1}`.padEnd(5) + a).join("\n");
    return `could not compile shader ${this.name}: ${info}, \n${numberedSource}`;
  }

  adopt(key, linked) {
    this.program = linked.program;
    this.vs = linked.vs;
    this.fs = linked.fs;
    this.attributeLocations = linked.attributeLocations;
    this.uniformLocations = linked.uniformLocations;
    this.uniformBlockIndices = linked.uniformBlockIndices;
    this.uniformBlocks = linked.uniformBlocks;
    this.uniforms = linked.uniforms;
    this.programKey = key;
  }

  setUniformMatrix4(name, value) {
    const gl = this.gl;
    const location = this.uniformLocations[name];

    if (location == null) {
      return;
    }

    let tmp = new Float32Array(value.elements);
    gl.uniformMatrix4fv(location, false, tmp);
  }

  setUniform1f(name, value) {
    const gl = this.gl;
    const uniform = this.uniforms[name];

    if (uniform === undefined) {
      return;
    }

    if (uniform.value === value) {
      return;
    }

    uniform.value = value;

    gl.uniform1f(uniform.location, value);
  }

  setUniformBoolean(name, value) {
    const gl = this.gl;
    const uniform = this.uniforms[name];

    if (uniform === undefined) {
      return;
    }

    if (uniform.value === value) {
      return;
    }

    uniform.value = value;

    gl.uniform1i(uniform.location, value);
  }

  setUniform2f(name, value) {
    const gl = this.gl;
    const location = this.uniformLocations[name];

    if (location == null) {
      return;
    }

    gl.uniform2f(location, value[0], value[1]);
  }

  setUniform3f(name, value) {
    const gl = this.gl;
    const location = this.uniformLocations[name];

    if (location == null) {
      return;
    }

    gl.uniform3f(location, value[0], value[1], value[2]);
  }

  setUniform(name, value) {

    if (value && value.isMatrix4) {
      this.setUniformMatrix4(name, value);
    } else if (typeof value === "number") {
      this.setUniform1f(name, value);
    } else if (typeof value === "boolean") {
      this.setUniformBoolean(name, value);
    } else if (value instanceof Array) {

      if (value.length === 2) {
        this.setUniform2f(name, value);
      } else if (value.length === 3) {
        this.setUniform3f(name, value);
      }

    } else {
      console.error("unhandled uniform type: ", name, value);
    }

  }

  setUniform1i(name, value) {
    let gl = this.gl;
    let location = this.uniformLocations[name];

    if (location == null) {
      return;
    }

    gl.uniform1i(location, value);
  }

};
